<!-- scrollbar-gutter keeps the layout width identical whether or not the
     content scrolls, and the content is top-anchored (no justify-center):
     both guarantee the header/toolbar never shift when the grid/list toggle
     changes the content height. -->
<div
  class="absolute inset-0 flex flex-col min-w-0 overflow-auto [scrollbar-gutter:stable] dark:bg-[#0d0e12] pc-page-gradient pc-shell-ground"
>
  <div class="min-h-full font-sans text-slate-800 dark:text-neutral-200 relative flex flex-col">
    <!-- Header: floating glass bar (same recipe as the classy layout nav-bar).
         Intentional differences vs classy: `fixed` because this page scrolls
         the whole viewport (classy scrolls an inner container), the product
         logo because no instance is selected yet (workspace branding doesn't
         exist here), and no notifications bell (its inbox needs an instance
         context). -->
    <header
      class="fixed top-2 left-2 right-2 z-49 h-14 flex items-center px-4 md:px-6 print:hidden rounded-2xl nav-bar-floating backdrop-blur-2xl backdrop-saturate-150"
    >
      <div class="flex items-center w-full">
        <a href="/" class="shrink-0">
          <img
            class="w-auto h-auto max-w-50 max-h-10"
            src="assets/images/logo/logo-primecontrol.png"
            alt="Logo"
          />
        </a>
      </div>
      <div class="flex items-center pl-2 ml-auto space-x-0.5 sm:space-x-2">
        <primecontrol-translations></primecontrol-translations>
        <user [showAvatar]="false"></user>
      </div>
    </header>

    <main class="w-full px-4 sm:px-12 pt-20 pb-12 relative z-10">
      <!-- Greeting + action toolbar (brandbot style) -->
      <div class="flex items-center justify-between gap-4 mb-6 mt-2 flex-wrap">
        <div class="min-w-0 flex-1">
          <h1
            class="pc-hero-enter text-2xl md:text-3xl font-bold text-slate-700 dark:text-white mb-1 tracking-tight"
          >
            {{ isNationalGreeting ? greeting : (greeting | translate) }}, {{ user()?.firstName }}
            {{ user()?.lastName
            }}<span class="pc-hero-emoji inline-block ml-1">{{ greetingEmoji }}</span>
          </h1>
          <!-- min-h reserves the line box while the translate pipe is still
               empty (catalog loads over HTTP), so the grid doesn't jump down
               when the subtitle text lands. -->
          <p
            class="pc-hero-enter pc-hero-enter-delayed min-h-6 md:min-h-7 text-slate-500 dark:text-neutral-400 text-pc-body md:text-pc-title-sm font-medium"
          >
            {{ 'INSTANCE_SELECTOR_SUBTITLE' | translate }}
          </p>
        </div>

        <!-- Same controls, sizes and order as every module list toolbar
             (primecontrol-list-page-header): search, view toggle, add. -->
        <div
          class="flex flex-wrap items-center gap-2 sm:gap-3 w-full md:w-auto md:flex-nowrap md:justify-end"
        >
          <!-- keydown on the wrapper: Enter opens the first result,
               ArrowDown moves focus into the grid (see onSearchKeydown). -->
          <div
            class="w-full sm:w-64 md:w-72 lg:w-80 sm:flex-none"
            (keydown)="onSearchKeydown($event)"
          >
            <primecontrol-search
              class="w-full"
              [control]="searchInputControl"
              (search)="onSearch()"
            ></primecontrol-search>
          </div>

          <primecontrol-view-toggle-group
            [activeTab]="viewMode()"
            (activeTabChange)="viewMode.set($event)"
          />

          @if (canCreateInstance()) {
            <primecontrol-add-button
              class="shrink-0 ml-auto sm:ml-0"
              [label]="'ADD_INSTANCE'"
              (event)="openInstance()"
            ></primecontrol-add-button>
          }
        </div>
      </div>

      <div class="min-h-[400px]">
        @if (viewMode() === 'grid') {
          <!-- GRID VIEW -->
          <div
            class="w-full grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5 gap-5"
          >
            @if (loading) {
              @for (i of skeletonItems; track i) {
                <article
                  class="pc-glass-card pc-skeleton-card w-full aspect-[8/5]"
                  aria-hidden="true"
                >
                  <div class="pc-skeleton-aurora"></div>
                  <div class="pc-skeleton-ring"></div>
                  <!-- Concentric tile: soft outer square + pulsing core emitting a ripple -->
                  <div class="relative z-10 w-full h-full flex items-center justify-center">
                    <div class="pc-skeleton-tile">
                      <div class="pc-skeleton-tile-core"></div>
                    </div>
                  </div>
                </article>
              }
            } @else {
              @for (instance of instances; track trackByFn($index, instance)) {
                <article
                  class="pc-glass-card group cursor-pointer aspect-[8/5]"
                  [ngClass]="{
                    'pc-card-enter': !cardsSettled(),
                    'pc-card-selected': selectingId() === instance.id,
                    'pc-card-dimmed': selectingId() !== null && selectingId() !== instance.id,
                  }"
                  role="button"
                  tabindex="0"
                  data-instance-card
                  [attr.data-instance-id]="instance.id"
                  [attr.aria-label]="instance.instanceName"
                  (click)="instanceSelect(instance)"
                  (keydown)="onCardKeydown($event, instance)"
                >
                  <!-- Top-right controls: the favorite star, then the actions menu.
                       Both are revealed on hover; a starred card keeps its star
                       showing. On touch screens (no hover) they always show.
                       The menu is a horizontal ellipsis, as every other action menu
                       in the app (see primecontrol-main-table's actions cell), and
                       only where the user may edit the workspace settings. -->
                  <div
                    class="absolute top-3 right-3 z-30 flex items-center gap-0.5"
                    (click)="$event.stopPropagation()"
                  >
                    <ng-container
                      *ngTemplateOutlet="favoriteStar; context: { $implicit: instance }"
                    ></ng-container>
                    @if (canEditSettings(instance)) {
                      <div
                        class="transition-opacity duration-200 pointer-fine:opacity-0 pointer-fine:group-hover:opacity-100 pointer-fine:group-focus-within:opacity-100"
                      >
                        <primecontrol-pop-up-button
                          class="z-40"
                          [menuOptions]="cardMenuOptions"
                          [data]="instance"
                          icon="ellipsis"
                          [ref]="'table'"
                        />
                      </div>
                    }
                  </div>

                  <!-- Membership layout: logo top-left, engraved name bottom-left -->
                  <div class="relative z-10 w-full h-full flex flex-col justify-between p-5 md:p-6">
                    <div class="flex items-center h-16 max-w-[80%]">
                      @if (instance.preference?.square_logo) {
                        <img
                          [src]="instance.preference?.square_logo"
                          alt=""
                          draggable="false"
                          class="pc-logo-fade max-h-16 max-w-full object-contain object-left"
                          (load)="onLogoLoad($event)"
                          (contextmenu)="$event.preventDefault()"
                        />
                      } @else {
                        <primecontrol-svg-icon
                          svgIcon="mat_solid:smart_toy"
                          class="icon-size-12 text-neutral-400"
                        ></primecontrol-svg-icon>
                      }
                    </div>
                    <div class="min-w-0">
                      <!-- Engraved eyebrow: crossfades to the last-access
                           metadata on hover when this browser has entered
                           the workspace before. It sits above the name/badge
                           row so the hover label spans the full card width
                           instead of truncating at the badge column. -->
                      @if (instance._lastAccessLabel) {
                        <div
                          class="relative text-pc-eyebrow font-semibold tracking-[0.14em] uppercase text-slate-400 dark:text-neutral-500 mb-0.5"
                        >
                          <span
                            class="block truncate transition-opacity duration-200 opacity-0 pointer-fine:opacity-100 pointer-fine:group-hover:opacity-0"
                          >
                            {{ 'WORKSPACE' | translate }}
                          </span>
                          <span
                            class="absolute inset-0 truncate transition-opacity duration-200 pointer-fine:opacity-0 pointer-fine:group-hover:opacity-100"
                          >
                            {{ 'LAST_ACCESS' | translate }} · {{ instance._lastAccessLabel }}
                          </span>
                        </div>
                      } @else {
                        <div
                          class="text-pc-eyebrow font-semibold tracking-[0.14em] uppercase text-slate-400 dark:text-neutral-500 mb-0.5"
                        >
                          {{ 'WORKSPACE' | translate }}
                        </div>
                      }
                      <div class="flex items-center justify-between gap-2 min-w-0">
                        <h3
                          class="min-w-0 font-semibold text-slate-600 dark:text-neutral-100 text-pc-title-sm tracking-tight dark:group-hover:text-white transition-colors truncate"
                        >
                          {{ instance.instanceName }}
                        </h3>
                        @if (instance.id === recentInstanceId) {
                          <span class="pc-recent-badge shrink-0">
                            {{ 'RECENT' | translate }}
                          </span>
                        }
                      </div>
                    </div>
                  </div>
                </article>
              } @empty {
                <div class="col-span-full">
                  <ng-container *ngTemplateOutlet="emptyState"></ng-container>
                </div>
              }
            }
          </div>
        } @else {
          <!-- LIST VIEW -->
          <div
            class="w-full bg-white dark:bg-neutral-900 rounded-2xl border border-slate-200 dark:border-neutral-800 overflow-hidden shadow-sm"
          >
            @if (loading) {
              @for (i of skeletonItems; track i) {
                <div
                  class="flex items-center gap-4 px-4 py-2.5 border-b border-slate-100 dark:border-neutral-800 last:border-b-0"
                  aria-hidden="true"
                >
                  <div
                    class="w-9 h-9 rounded-lg bg-slate-100 dark:bg-neutral-800 animate-pulse"
                  ></div>
                  <div
                    class="flex-1 h-4 bg-slate-100 dark:bg-neutral-800 rounded animate-pulse"
                  ></div>
                </div>
              }
            } @else {
              @for (instance of instances; track trackByFn($index, instance)) {
                <div
                  class="group select-none flex items-center gap-4 px-4 py-2.5 border-b border-slate-100 dark:border-neutral-800 last:border-b-0 cursor-pointer hover:bg-slate-50 dark:hover:bg-neutral-800/40 transition-all outline-none focus-visible:outline-2 focus-visible:outline-slate-400/60 focus-visible:-outline-offset-2"
                  [ngClass]="{
                    'opacity-40 pointer-events-none':
                      selectingId() !== null && selectingId() !== instance.id,
                    'bg-slate-50 dark:bg-neutral-800/40': selectingId() === instance.id,
                  }"
                  role="button"
                  tabindex="0"
                  data-instance-card
                  [attr.data-instance-id]="instance.id"
                  [attr.aria-label]="instance.instanceName"
                  (click)="instanceSelect(instance)"
                  (keydown)="onCardKeydown($event, instance)"
                >
                  <div
                    class="w-9 h-9 rounded-lg bg-slate-50 dark:bg-neutral-800/50 border border-slate-200 dark:border-neutral-700 flex items-center justify-center shrink-0 overflow-hidden"
                  >
                    @if (instance.preference?.square_logo) {
                      <img
                        [src]="instance.preference?.square_logo"
                        alt=""
                        draggable="false"
                        class="pc-logo-fade w-full h-full object-contain"
                        (load)="onLogoLoad($event)"
                        (contextmenu)="$event.preventDefault()"
                      />
                    } @else {
                      <primecontrol-svg-icon
                        svgIcon="mat_solid:smart_toy"
                        class="icon-size-5 text-slate-400"
                      ></primecontrol-svg-icon>
                    }
                  </div>
                  <div class="flex-1 min-w-0 flex items-center gap-2">
                    <h3
                      class="font-medium text-slate-600 dark:text-neutral-200 text-pc-secondary truncate dark:group-hover:text-white transition-colors"
                    >
                      {{ instance.instanceName }}
                    </h3>
                    @if (instance.id === recentInstanceId) {
                      <span class="pc-recent-badge shrink-0">
                        {{ 'RECENT' | translate }}
                      </span>
                    }
                  </div>
                  @if (instance._lastAccessLabel) {
                    <span
                      class="hidden sm:block shrink-0 text-pc-eyebrow font-semibold uppercase tracking-[0.14em] text-slate-400 dark:text-neutral-500 pointer-fine:opacity-0 pointer-fine:group-hover:opacity-100 transition-opacity duration-200"
                    >
                      {{ 'LAST_ACCESS' | translate }} · {{ instance._lastAccessLabel }}
                    </span>
                  }
                  <div (click)="$event.stopPropagation()" class="shrink-0">
                    <ng-container
                      *ngTemplateOutlet="favoriteStar; context: { $implicit: instance }"
                    ></ng-container>
                  </div>
                  <!-- Fixed-width slot: rows without the menu keep the chevrons aligned. -->
                  <div (click)="$event.stopPropagation()" class="shrink-0 w-8 flex justify-center">
                    @if (canEditSettings(instance)) {
                      <primecontrol-pop-up-button
                        class="z-40"
                        [menuOptions]="cardMenuOptions"
                        [data]="instance"
                        icon="ellipsis"
                        [ref]="'table'"
                      />
                    }
                  </div>
                  <primecontrol-svg-icon
                    class="icon-size-4 text-slate-300 dark:text-neutral-500 group-hover:text-slate-500 dark:group-hover:text-neutral-300 transition-colors shrink-0"
                    [svgIcon]="'heroicons_solid:chevron-right'"
                  ></primecontrol-svg-icon>
                </div>
              } @empty {
                <ng-container *ngTemplateOutlet="emptyState"></ng-container>
              }
            }
          </div>
        }

        <!-- Only when the instances don't fit one page: without it, every
             workspace past the page size was unreachable except by searching. -->
        @if (pagination?.totalItems > pageSize || pageIndex > 0) {
          <primecontrol-table-paginator
            class="mt-6"
            [ngClass]="{ 'pointer-events-none': isLoading }"
            [length]="pagination.totalItems"
            [pageIndex]="pageIndex"
            [pageSize]="pageSize"
            [pageSizeOptions]="[25, 50, 100]"
            (page)="onPage($event)"
          ></primecontrol-table-paginator>
        }
      </div>
    </main>
  </div>
</div>

<!-- Favorite star (grid and list): outline and revealed on hover until the
     workspace is starred, then filled and always visible. Shown at all times
     on touch screens, which have no hover. -->
<ng-template #favoriteStar let-instance>
  <button
    type="button"
    class="pc-favorite-star"
    [class.pc-favorite-star-on]="instance.isFavorite"
    [class.pc-favorite-star-pop]="poppedId() === instance.id"
    [attr.aria-pressed]="!!instance.isFavorite"
    [attr.aria-label]="(instance.isFavorite ? 'FAVORITE_REMOVE' : 'FAVORITE_ADD') | translate"
    [primecontrolTooltip]="(instance.isFavorite ? 'FAVORITE_REMOVE' : 'FAVORITE_ADD') | translate"
    (click)="toggleFavorite(instance, $event)"
  >
    <primecontrol-svg-icon
      class="icon-size-4"
      svgIcon="heroicons_outline:star"
    ></primecontrol-svg-icon>
  </button>
</ng-template>

<!-- Shared empty state (grid and list): crafted like the notifications
     popover's (icon tile, title, hint) and search-aware — a fruitless search
     reads differently than having no instances at all. -->
<ng-template #emptyState>
  <div class="flex flex-col items-center justify-center text-center px-6 py-16">
    <div
      class="flex items-center justify-center w-14 h-14 rounded-2xl bg-slate-100 dark:bg-neutral-900 mb-4"
    >
      <primecontrol-svg-icon
        class="icon-size-6 text-slate-400 dark:text-neutral-500"
        [svgIcon]="
          searchInputControl.value ? 'heroicons_solid:search' : 'heroicons_outline:template'
        "
      ></primecontrol-svg-icon>
    </div>
    <p class="text-pc-secondary font-semibold text-slate-800 dark:text-neutral-200 mb-1">
      {{ (searchInputControl.value ? 'NO_RESULTS' : 'NO_INSTANCES') | translate }}
    </p>
    <p class="text-pc-body leading-relaxed text-slate-500 dark:text-neutral-400 max-w-64">
      {{ (searchInputControl.value ? 'NO_RESULTS_HINT' : 'NO_INSTANCES_HINT') | translate }}
    </p>
  </div>
</ng-template>
